/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import * as React from 'react';

import { ReadOnlyFormGroup } from 'components/common';
import type { CreateInvestigationNotificationSummaryType } from 'security-app/types';
import { usePagination } from 'security-app/components/common/contexts';
import {
  useGetInvestigations,
  useGetUsers,
  useGetTeams,
  useGetPriorities,
} from 'security-app/hooks/useInvestigationsAPI';

function CreateInvestigationNotificationDetails({
  notification,
}: CreateInvestigationNotificationSummaryType) {
  const localPagination = usePagination();

  const { investigations } = useGetInvestigations(localPagination);
  const investigationsMap = React.useMemo(() => ({
    ...investigations.reduce((acc, investigation) => ({
      ...acc,
      [investigation.id]: { label: investigation.name },
    }), {}),
  }), [investigations]);

  const { users } = useGetUsers(true);
  const { teams } = useGetTeams(true);
  const usersTeamsMap = React.useMemo(() => ({
    ...users.reduce((acc, user) => ({ ...acc, [user.id]: { label: user.full_name, icon: 'user' } }), {}),
    ...teams.reduce((acc, team) => ({ ...acc, [team.id]: { label: team.name, icon: 'users' } }), {}),
  }), [users, teams]);

  const { priorities } = useGetPriorities(true);
  const getPriorityText = React.useCallback((value: number) => (value ? priorities.find((priority) => priority.priority === value)?.text : ''), [priorities]);

  return (
    <>
      <ReadOnlyFormGroup label="Create a New Investigation for Every Alert"
                         value={notification.config.create_new_investigation ? <em>True</em> : <em>False</em>} />
      {!notification.config.create_new_investigation && (
        <ReadOnlyFormGroup label="Investigation"
                           value={investigationsMap[notification.config.investigation_id]?.label} />
      )}
      <ReadOnlyFormGroup label="Assign Investigation To"
                         value={usersTeamsMap[notification.config.investigation_assigned_to]?.label} />
      <ReadOnlyFormGroup label="Investigation Priority"
                         value={getPriorityText(notification.config.investigation_priority)} />
      <ReadOnlyFormGroup label="Investigation Status" value={notification.config.investigation_status} />
    </>
  );
}

CreateInvestigationNotificationDetails.defaultProps = {
  notification: {},
};

export default CreateInvestigationNotificationDetails;
